import React, { useState } from 'react';
import { Modal, Button } from '../ui';
import { X, Combine } from 'lucide-react';
import type { Table } from '../../types/@types/table';

interface JoinTablesModalProps {
  open: boolean;
  tables: Table[];
  onClose: () => void;
  onConfirm: (primaryId: string, secondaryId: string) => void;
  loading?: boolean;
}

export const JoinTablesModal: React.FC<JoinTablesModalProps> = ({
  open,
  tables,
  onClose,
  onConfirm,
  loading,
}) => {
  const [primaryId, setPrimaryId] = useState<string>('');
  const [secondaryId, setSecondaryId] = useState<string>('');

  const handleConfirm = () => {
    if (!primaryId || !secondaryId || primaryId === secondaryId) return;
    onConfirm(primaryId, secondaryId);
  };

  const availableTables = tables.filter(t => t.status === 'Disponible' || t.status === 'Ocupada');

  return (
    <Modal open={open} onClose={onClose} titleId="join-modal-title" className="max-w-md">
      <div className="flex items-start justify-between border-b border-border px-6 py-4">
        <div>
          <h2 id="join-modal-title" className="text-lg font-semibold text-text">
            Unir Mesas
          </h2>
          <p className="text-sm text-text-muted">Selecciona las mesas para unir en un solo pedido</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-9 w-9 rounded-full">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="p-6 space-y-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text mb-2">Mesa Principal (Session Owner)</label>
            <div className="grid grid-cols-4 gap-2">
              {availableTables.map((t) => (
                <button
                  key={`primary-${t.id}`}
                  onClick={() => setPrimaryId(t.id)}
                  className={`p-2 rounded-md border text-sm font-semibold transition-colors ${
                    primaryId === t.id 
                      ? 'bg-blue-600 border-blue-600 text-white shadow-md' 
                      : 'bg-white border-border text-text hover:bg-gray-50'
                  }`}
                >
                  #{t.id}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text mb-2">Mesa a Unir (Secondary)</label>
            <div className="grid grid-cols-4 gap-2">
              {availableTables.filter(t => t.id !== primaryId).map((t) => (
                <button
                  key={`secondary-${t.id}`}
                  onClick={() => setSecondaryId(t.id)}
                  className={`p-2 rounded-md border text-sm font-semibold transition-colors ${
                    secondaryId === t.id 
                      ? 'bg-green-600 border-green-600 text-white shadow-md' 
                      : 'bg-white border-border text-text hover:bg-gray-50'
                  }`}
                >
                  #{t.id}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-border px-6 py-4 flex gap-3 justify-end">
        <Button variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button 
          onClick={handleConfirm} 
          disabled={!primaryId || !secondaryId || primaryId === secondaryId || loading}
          isLoading={loading}
        >
          <Combine className="mr-2 h-4 w-4" />
          Unir Mesas Ahora
        </Button>
      </div>
    </Modal>
  );
};
